'use client';

import { useState } from 'react';

/**
 * The floating "walk the product as…" control on every preview page: one click
 * into the panel each staff role sees or the app each customer kind sees, via the
 * same demo-login routes the reference door uses. Gated by a demo flag the build
 * bakes (see `enabled`), never on production, so it never renders there.
 */
const STAFF: ReadonlyArray<{ as: string; label: string; cls: string }> = [
  { as: 'admin', label: 'Admin', cls: 'demo-door-admin' },
  { as: 'supervisor', label: 'Supervisor', cls: 'demo-door-supervisor' },
  { as: 'support', label: 'Support', cls: 'demo-door-support' },
  { as: 'financier', label: 'Financier', cls: 'demo-door-financier' },
  { as: 'developer', label: 'Developer', cls: 'demo-door-supervisor' },
];

const CUSTOMER: ReadonlyArray<{ as: 'client' | 'agency' | 'linked'; label: string; cls: string }> = [
  { as: 'client', label: 'Creator', cls: 'demo-door-client' },
  { as: 'linked', label: 'Linked creator', cls: 'demo-door-client' },
  { as: 'agency', label: 'Agency', cls: 'demo-door-agency' },
];

export function DemoRoleSwitcher({ app }: { app: 'admin' | 'web' }) {
  // Always on, except production. Only prod sets NEXT_PUBLIC_APP_ENV=production
  // (mirrors the server's APP_ENV that isIndexable reads), so dev, previews and
  // local all show it. The live demo-login door is gated off prod server-side
  // too, so this is belt-and-suspenders.
  const enabled = process.env.NEXT_PUBLIC_APP_ENV !== 'production';
  const [open, setOpen] = useState(false);
  if (!enabled) return null;

  // The panel's host is the client host with `ops-` in front; the client host is
  // the panel's with it removed. Same preview label, sibling subdomains.
  const crossHost = (strip: boolean) => {
    const { protocol, host } = window.location;
    const other = strip ? host.replace(/^ops-/, '') : `ops-${host.replace(/^ops-/, '')}`;
    return `${protocol}//${other}`;
  };

  const staffHref = (as: string) => {
    const path = as === 'admin' ? '/demo-login' : `/demo-login/staff?as=${as}`;
    return app === 'admin' ? path : `${crossHost(false)}${path}`;
  };
  const customerHref = (as: 'client' | 'agency' | 'linked') => {
    const path = `/demo-login?as=${as}`;
    return app === 'web' ? path : `${crossHost(true)}${path}`;
  };

  return (
    <div className={`demo-switch${open ? ' is-open' : ''}`}>
      {open && (
        <div className="demo-switch-menu" role="menu">
          <p className="demo-switch-head">Walk the product as</p>
          {STAFF.map((r) => (
            <a key={r.as} href={staffHref(r.as)} className={`btn btn-block demo-door ${r.cls}`} role="menuitem">
              {r.label}
            </a>
          ))}
          {CUSTOMER.map((r) => (
            <a key={r.as} href={customerHref(r.as)} className={`btn btn-block demo-door ${r.cls}`} role="menuitem">
              {r.label}
            </a>
          ))}
        </div>
      )}
      <button
        type="button"
        className="demo-switch-toggle"
        aria-expanded={open}
        aria-label={open ? 'Close' : 'Demo: switch role'}
        title={open ? 'Close' : 'Demo: switch role'}
        onClick={() => setOpen((v) => !v)}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          {open ? (
            <path d="M18 6 6 18M6 6l12 12" />
          ) : (
            <>
              <circle cx="9" cy="8" r="3.5" />
              <path d="M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6" />
              <path d="m17 4 3 3-3 3M20 7h-4" />
            </>
          )}
        </svg>
      </button>
    </div>
  );
}
